<template>
  <WdsStack direction-column gap-custom="2rem">
    <WdsStack direction-row>
      <div class="tw-basis-1/2"><WdsText :text="$t('Engagements')" secondary /></div>
      <div class="tw-basis-1/2">
        <WdsText :text="`${acceptedJobsCount} ${$t('accepted')}`" primary />
      </div>
    </WdsStack>

    <WdsStack direction-row>
      <div class="tw-basis-1/2"><WdsText :text="$t('Relationship')" secondary /></div>
      <WdsStack direction-row items-center gap-xs class="tw-basis-1/2">
        <WdsText :text="`${invitedAtElapsed}`" primary />
        <WdsText :sub-text="`(${invitedAtLong})`" secondary />
      </WdsStack>
    </WdsStack>

    <WdsStack v-if="user.hasPermission('access-all-jobs')" gap-custom="2rem" direction-column>
      <WdsStack direction-row>
        <div class="tw-basis-1/2"><WdsText :text="$t('Total paid')" secondary /></div>
        <div class="tw-basis-1/2">
          <WdsTooltip :text="`Converted from actual payment currencies to ${accountCurrency}. Totals may vary slightly due to exchange rate changes.`">
            <WdsText primary>
              ~{{ totalPaid }}
            </WdsText>
          </WdsTooltip>
      </div>
      </WdsStack>

      <WorkerHiresList :hires="activeHires" :title="$t('Active jobs')" />

      <WorkerHiresList :hires="completedHires" :title="$t('Completed jobs')" />
    </WdsStack>

    <WdsStack v-if="conversationsList.length > 0" direction-column gap-md>
      <WdsText :text="$t('Active conversations')" secondary />
      <a
        v-for="conversation in conversationsList"
        :key="conversation.id"
        :href="laravelNamedRoute(namedRoutes.messages.show, { id: conversation.id })"
        class="-tw-ml-1"
      >
        <WdsButtonGhost
          class="tw-w-full tw-text-left"
          :data-test="`tc-activity-conversation-${conversationsList.indexOf(conversation)}`"
        >
          <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
            <WdsStack gap-md align-center>
              <div class="tw-bg-true-gray-100 tw-p-3.5 tw-rounded">
                <WdsIcon :size="16" name="chat" class="tw-text-gray-600" />
              </div>
              <WdsText
                :text="conversation.subject"
                :sub-text="`${
                  conversation.latestMessage
                    ? `With ${conversation.latestMessage?.author?.name} on`
                    : 'On'
                } ${conversation.createdAt}`"
              />
            </WdsStack>
            <WdsIcon :size="16" name="chevron-right" class="tw-text-gray-600" />
          </div>
        </WdsButtonGhost>
      </a>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import { defineProps, computed } from 'vue'
import { WdsStack, WdsIcon, WdsText, WdsButtonGhost, WdsTooltip } from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import {
  CONVERSATIONS_VIEW_FRAGMENT,
  WORKER_VIEW_FRAGMENT,
} from '@/components/WorkerDetailPanel/graphql/fragments'
import { toFormattedAmountByLocale } from '@/utils/currency'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import WorkerHiresList from './WorkerHiresList.vue'
import useDate from '@/composables/useDate'
import { useLocaleTimeAgo } from '@/composables/useLocaleTimeAgo'
import { FragmentType, useFragment } from '@/gql'
import { user } from '@/composables/useUser'
import { TrustedContactViewFragmentFragment } from '@/gql/graphql'

const props = defineProps<{
  worker?: FragmentType<typeof WORKER_VIEW_FRAGMENT> | null
  trustedContact?: TrustedContactViewFragmentFragment | null
  conversations: FragmentType<typeof CONVERSATIONS_VIEW_FRAGMENT>[]
}>()

const accountCurrency = computed(() => currentAccount.currency)
const workerFragment = computed(() => useFragment(WORKER_VIEW_FRAGMENT, props.worker))
const acceptedJobsCount = computed(() => workerFragment.value?.hires?.paginatorInfo?.total ?? 0)
const activeHires = computed(() => workerFragment.value?.activeHires?.data ?? [])
const completedHires = computed(() => workerFragment.value?.completedHires?.data ?? [])
const totalPaid = computed(() => {
  const total = toFormattedAmountByLocale((workerFragment.value?.totalPaid ?? 0).toFixed(2))
  return `${total} ${accountCurrency.value}`
})

const conversationsList = computed(() =>
  useFragment(CONVERSATIONS_VIEW_FRAGMENT, props.conversations),
)

const invitedAtElapsed = useLocaleTimeAgo(props.trustedContact?.invitedAt)
const { date: invitedAtLong } = useDate(props.trustedContact?.invitedAt, 'MMMM, YYYY')
</script>
